/**
 * TypeBadge Component
 * 
 * Displays type/category badges with consistent styling.
 * Used for transport protocols, peer types, codec types, etc.
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';

export interface TypeBadgeProps {
  /** Badge label */
  label: string;
  /** Color variant */
  variant?: 'blue' | 'green' | 'purple' | 'orange' | 'gray' | 'red' | 'yellow';
  /** Size variant */
  size?: 'sm' | 'md';
  /** Optional icon */
  icon?: LucideIcon;
}

const variantClasses = {
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-green-100 text-green-800',
  purple: 'bg-purple-100 text-purple-800',
  orange: 'bg-orange-100 text-orange-800',
  gray: 'bg-gray-100 text-gray-800',
  red: 'bg-red-100 text-red-800',
  yellow: 'bg-yellow-100 text-yellow-800',
};

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-1 text-xs',
};

export function TypeBadge({ 
  label, 
  variant = 'gray', 
  size = 'md',
  icon: Icon,
}: TypeBadgeProps) {
  return (
    <span 
      className={`inline-flex items-center gap-1 ${sizeClasses[size]} font-medium rounded ${variantClasses[variant]}`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </span>
  );
}

/**
 * TransportBadge - Pre-configured badge for transport protocols
 */
export interface TransportBadgeProps {
  transport: 'TLS' | 'TCP' | 'UDP' | 'WS' | 'WSS' | string;
  size?: 'sm' | 'md';
}

const transportVariants: Record<string, TypeBadgeProps['variant']> = {
  TLS: 'green',
  TCP: 'blue',
  UDP: 'orange',
  WS: 'purple',
  WSS: 'green',
};

export function TransportBadge({ transport, size = 'md' }: TransportBadgeProps) {
  const variant = transportVariants[transport.toUpperCase()] || 'gray';
  return <TypeBadge label={transport.toUpperCase()} variant={variant} size={size} />;
}

/**
 * PeerTypeBadge - Pre-configured badge for peer types
 */
export interface PeerTypeBadgeProps {
  type: 'teams' | 'carrier' | 'webrtc' | 'softphone' | 'pbx' | string;
  size?: 'sm' | 'md';
}

const peerTypeConfig: Record<string, { label: string; variant: TypeBadgeProps['variant'] }> = {
  teams: { label: 'Microsoft Teams', variant: 'blue' },
  carrier: { label: 'PSTN Carrier', variant: 'green' },
  webrtc: { label: 'WebRTC', variant: 'purple' },
  softphone: { label: 'Softphone', variant: 'orange' },
  pbx: { label: 'PBX System', variant: 'gray' },
};

export function PeerTypeBadge({ type, size = 'md' }: PeerTypeBadgeProps) {
  const config = peerTypeConfig[type] || { label: type, variant: 'gray' as const };
  return <TypeBadge label={config.label} variant={config.variant} size={size} />;
}
